/**
 * useHiddenFigures - the people only the GM can see (plan map-display, G1).
 *
 * A hidden figure lives in the GM-only `gmHidden` collection, one document per
 * figure, never on the shared roster (ADR 2026-10-07). On the GM's screen it is
 * part of the WORKING roster - so choosing it, attacking with it and editing it
 * need nothing new - and this hook keeps the two apart:
 *
 *   - IN: the collection is watched (GM only). A figure it shows is put into
 *     the working roster; one it REMOVES (explicitly - an absence is never a
 *     removal, gotchas 9 and 13) is forgotten, and so is one whose record is a
 *     TOMBSTONE: an ended figure keeps its alias stored so none is reused. A
 *     read OLDER than this screen's last write of that figure is a late echo
 *     and is ignored (applying it would move the token back); a record whose
 *     text is what this screen last wrote or read is skipped unparsed.
 *   - OUT: a hidden figure edited on this screen (its state after a hit, its
 *     sheet, its token) is written back to its document once per burst. Every
 *     write to `gmHidden` goes through ONE writer (`saveFigure`).
 *   - THE SEAM: the sync layer is handed only `sharedRoster` and a
 *     `setSharedRoster` that never lets a hidden figure in or out of the
 *     shared world. With nothing hidden both are the roster's own, so a
 *     player's screen - and a GM's without secrets - sees no change at all.
 *
 * Known limit: a figure's combatant edited on ANOTHER of the GM's screens
 * reaches this one's record but not its working roster (adds only).
 *
 * @param {object} args - sessionId, isGm, roster, setRoster, rosterTabs, mp
 * @returns {{ sharedRoster, setSharedRoster, sharedTabs, hiddenIds, isHidden, aliasOf, figures, figuresRef,
 *   hiddenTokens, placeToken, adopt, reserve, restore, endStored, saveFigure, takenAliases, claimAlias,
 *   strayRef (set by the shell to the raw remover: a shared copy of a hidden figure is deleted) }}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { aliasFor, splitHidden, withHidden, readHiddenDoc, figureToDoc, endedToDoc } from '../engine/roster/hidden';
import { diag, errorSummary } from '../diagnostics/log';
import useMirror from './useMirror';

const NONE = new Map();
const NO_IDS = new Set();
const NO_TOKENS = {};
/** How long an edit to a hidden figure waits before it is written: a hit or a drag writes in bursts. */
const WRITE_DELAY_MS = 350;

/**
 * One entry of a read, or null when there is nothing new in it: its text is
 * what this screen last wrote or read, or it is older than this screen's last
 * write (a late echo). Otherwise its alias is noted as taken.
 */
function readEntry({ id, data }, memory) {
  const raw = data?.data;
  if (raw === memory.lastRaw.get(id)) return null;
  if ((data?.updatedAt ?? 0) < (memory.writtenAt.get(id) ?? 0)) return null;
  memory.lastRaw.set(id, raw);
  const record = readHiddenDoc(data);
  if (record.alias) memory.aliases.add(record.alias);
  return record;
}

/**
 * The figures a read shows, and the ids it ended. An explicit removal or a
 * tombstone takes one out. Copy on write: a read that changes nothing gives
 * back the same map, so nothing downstream re-renders for it.
 */
function applyRead(prev, read, memory) {
  let next = prev;
  const own = () => (next === prev ? (next = new Map(prev)) : next);
  const ended = [...(read.removed ?? [])];
  for (const entry of read.entries ?? []) {
    const record = readEntry(entry, memory);
    if (record?.figure) own().set(entry.id, record.figure);
    else if (record?.ended) ended.push(entry.id);
  }
  const gone = ended.filter((id) => next.has(id));
  for (const id of gone) own().delete(id);
  return { next, gone };
}

/**
 * The figures, held in a ref that is always current (the verbs build on it
 * synchronously) and mirrored into state for rendering; `put` and `drop` copy
 * the map, change it and commit.
 */
function useFigureStore() {
  const [figures, setFigures] = useState(NONE);
  const ref = useRef(figures);
  const commit = useCallback((next) => {
    if (next === ref.current) return;
    ref.current = next;
    setFigures(next);
  }, []);
  const put = useCallback((...figs) => commit(figs.reduce((m, f) => m.set(f.id, f), new Map(ref.current))), [commit]);
  const drop = useCallback(
    (id) => {
      if (!ref.current.has(id)) return;
      const next = new Map(ref.current);
      next.delete(id);
      commit(next);
    },
    [commit],
  );
  return { figures, ref, commit, put, drop };
}

/** What this screen remembers across reads: its last write and read of each figure, and every alias seen. */
function useMemory() {
  return useRef({
    writtenAt: new Map(),
    lastRaw: new Map(),
    aliases: new Set(),
    /** The time of this screen's newest write of a figure, noted: an older echo of it is ignored. */
    stamp(id) {
      const at = Date.now();
      this.writtenAt.set(id, at);
      return at;
    },
  }).current;
}

/** THE ONE WRITER to `gmHidden`: resolves when stored; a refusal is logged and rethrown for a caller that waits. */
function useSave(synced, mp, sessionId, memory) {
  return useCallback(
    (id, doc) => {
      if (!synced) return Promise.resolve();
      // Its own echo carries this text: skipped unparsed (readEntry).
      memory.lastRaw.set(id, doc.data);
      return mp.setHiddenFigure(sessionId, id, doc).catch((e) => {
        diag.warn('hidden', `a hidden figure's record could not be stored (${id})`, errorSummary(e));
        throw e;
      });
    },
    [synced, mp, sessionId, memory],
  );
}

/**
 * The burst writer: every figure queued in a burst is written once, the
 * latest version of each - and what is still queued when the screen goes is
 * written then, not dropped.
 */
function useHiddenWriter(save) {
  const pending = useRef(new Map());
  const timer = useRef(null);
  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    const burst = [...pending.current.values()];
    pending.current.clear();
    for (const { fig, at } of burst) save(fig.id, figureToDoc(fig, at)).catch(() => {});
  }, [save]);
  const flushRef = useMirror(flush);
  useEffect(() => () => flushRef.current(), [flushRef]);
  const cancel = useCallback((id) => pending.current.delete(id), []);
  const queue = useCallback(
    (fig, at) => {
      pending.current.set(fig.id, { fig, at });
      if (!timer.current) timer.current = setTimeout(flush, WRITE_DELAY_MS);
    },
    [flush],
  );
  return useMemo(() => ({ queue, cancel }), [queue, cancel]);
}

/**
 * Watch the collection on the GM's screen: figures in, explicit removals out,
 * stale echoes ignored. A removed figure also leaves the working roster: it
 * was deleted or revealed on another of the GM's screens, and left in place it
 * would join the shared slice and be pushed. (A revealed one comes back with
 * the shared roster.)
 */
function useHiddenListener({ synced, mp, sessionId, store, memory, setRoster }) {
  const { ref, commit } = store;
  useEffect(() => {
    if (!synced) return undefined;
    return mp.onHiddenFiguresUpdate(sessionId, (read) => {
      const { next, gone } = applyRead(ref.current, read, memory);
      commit(next);
      if (gone.length) setRoster((prev) => prev.filter((c) => !gone.includes(c.id)));
    });
  }, [synced, mp, sessionId, ref, commit, memory, setRoster]);
}

/**
 * Put a stored figure that is not in the working roster into it (its combatant
 * as stored). Keyed on the figures alone - a roster change must not run it, or
 * a figure the GM just removed would be put straight back before its end lands -
 * and the roster is touched only when someone is missing. A figure `seen` in
 * the roster and missing now was REMOVED and is being ended (useDropRemoved):
 * never adopted, even when the figures changed in the same batch - a cut lifts
 * the leaving figure's token and replaces the roster at once (M6 playtest,
 * 2026-10-20: every hidden figure walked on into the next scene, unhidden).
 */
function useAdoptIntoRoster(figures, roster, setRoster, seen) {
  const rosterRef = useMirror(roster);
  useEffect(() => {
    // A reserved figure (born hidden) has no combatant until the roster add lands: nothing to adopt yet.
    const here = new Set(rosterRef.current.map((c) => c.id));
    const missing = [...figures.values()].filter((f) => f.combatant && !here.has(f.id) && !seen.current.has(f.id));
    if (!missing.length) return;
    setRoster((prev) => [...prev, ...missing.filter((f) => !prev.some((c) => c.id === f.id)).map((f) => f.combatant)]);
  }, [figures, rosterRef, setRoster, seen]);
}

/**
 * A hidden figure that WAS in the working roster and has left it was removed
 * by the GM: it is ended (tombstoned), or the listener would put it straight
 * back. Only after it has been seen there - a figure the listener has just
 * delivered is not in the roster until the adoption lands.
 */
function useDropRemoved(roster, figures, end, seen) {
  useEffect(() => {
    if (!figures.size) return;
    const present = new Set(roster.map((c) => c.id));
    for (const id of figures.keys()) {
      if (present.has(id)) seen.current.add(id);
      else if (seen.current.delete(id)) end(id);
    }
  }, [roster, figures, end, seen]);
}

/**
 * The two ways a figure stops being hidden; both cancel its queued write (it
 * would resurrect it) and leave a TOMBSTONE so its alias is never reused.
 *
 * - `end(id)`: forget it here at once, then store the tombstone - for a figure
 *   already gone from the roster, which the listener must not re-adopt.
 * - `endStored(id)`: store the tombstone FIRST, and only then forget it - for a
 *   reveal: if the server refuses, nothing has changed and the figure is still
 *   hidden (a live record left behind would hide it again on the next load).
 *   Rejects on a refusal, its pending edits queued again.
 */
function useEnd({ store, memory, writer, save }) {
  const { drop } = store;
  const stop = useStop(store, writer, memory);
  const end = useCallback(
    (id) => {
      const stopped = stop(id);
      if (!stopped) return Promise.resolve();
      drop(id);
      return save(id, endedToDoc(stopped.fig, stopped.at)).catch(() => {});
    },
    [stop, drop, save],
  );
  return { end, endStored: useEndStored(store, writer, stop, save) };
}

/** Cancel a figure's queued write and note the time: the figure as it stood and when, or null when not hidden. */
function useStop({ ref }, writer, memory) {
  return useCallback(
    (id) => {
      const fig = ref.current.get(id);
      if (!fig) return null;
      writer.cancel(id);
      return { fig, at: memory.stamp(id) };
    },
    [ref, writer, memory],
  );
}

/** `endStored` (see useEnd): the tombstone first, then forget; a refusal queues the figure's edits again. */
function useEndStored({ ref, drop }, writer, stop, save) {
  return useCallback(
    async (id) => {
      const stopped = stop(id);
      if (!stopped) return;
      try {
        await save(id, endedToDoc(stopped.fig, stopped.at));
      } catch (e) {
        writer.queue(ref.current.get(id) ?? stopped.fig, Date.now());
        throw e;
      }
      drop(id);
    },
    [ref, drop, writer, stop, save],
  );
}

/** `edit(id, patch)`: change one figure on this screen and queue its write. */
function useEdit(store, writer, memory) {
  const { ref, put } = store;
  return useCallback(
    (id, patch) => {
      const fig = ref.current.get(id);
      if (!fig) return;
      const next = { ...fig, ...patch };
      put(next);
      writer.queue(next, memory.stamp(id));
    },
    [ref, put, writer, memory],
  );
}

/**
 * A hidden figure's combatant changed in the working roster (a hit, a sheet
 * edit): its record follows. By identity - the object the record already
 * holds, or the last one this screen sent - never by serialising it.
 */
function useFollowRosterEdits(figures, hiddenPart, edit) {
  const sent = useRef(new Map());
  useEffect(() => {
    for (const c of hiddenPart) {
      const fig = figures.get(c.id);
      if (!fig || fig.combatant === c || sent.current.get(c.id) === c) continue;
      sent.current.set(c.id, c);
      edit(c.id, { combatant: c });
    }
  }, [figures, hiddenPart, edit]);
}

/**
 * A shared copy of a hidden figure - a player's late write re-created its
 * document after the hide deleted it, and no server rule can know who is
 * hidden - is deleted again by the GM's screen (`strayRef`, the sync layer's
 * raw remover, set by the shell). After the update, never inside it; a
 * repeat (React may run an updater twice) is one more idempotent delete.
 */
function reportStrays(next, ids, strayRef) {
  const strays = next.filter((c) => ids.has(c.id)).map((c) => c.id);
  if (strays.length) queueMicrotask(() => strays.forEach((id) => strayRef.current?.(id)));
}

/**
 * The seam: the shared slice of the working roster and of its tabs, and the
 * sync layer's setter. The setter is STABLE (the roster listener subscribes
 * once with the first one it is given), so the hidden ids are read through a
 * ref. Its updater sees only the shared slice; the hidden figures are put back
 * after it, and a shared copy of a hidden id (a reveal's own echo) is dropped.
 */
function useSharedSlice(roster, setRoster, rosterTabs, hiddenIds) {
  const idsRef = useMirror(hiddenIds);
  const strayRef = useRef(null);
  const split = useMemo(() => splitHidden(roster, hiddenIds), [roster, hiddenIds]);
  const sharedTabs = useMemo(
    () => (hiddenIds.size && rosterTabs ? rosterTabs.filter((t) => !hiddenIds.has(t.id)) : rosterTabs),
    [rosterTabs, hiddenIds],
  );
  const setSharedRoster = useCallback(
    (update) =>
      setRoster((prev) => {
        const ids = idsRef.current;
        const { shared, hidden } = splitHidden(prev, ids);
        const next = typeof update === 'function' ? update(shared) : update;
        if (next === shared) return prev;
        reportStrays(next, ids, strayRef);
        return withHidden(next, hidden, ids);
      }),
    [setRoster, idsRef],
  );
  return { sharedRoster: split.shared, hiddenPart: split.hidden, sharedTabs, setSharedRoster, strayRef };
}

/**
 * `adopt`: a figure becomes hidden on this screen (the caller has stored it, through `saveFigure`).
 * `saveFigure`: store one figure's record now, awaited - hide's first, write-first step.
 * `claimAlias` / `takenAliases`: every alias this session has used, live or ended - claimed the moment it is
 * chosen, so two hides in one round trip never share one.
 */
function useFigureVerbs({ put }, memory, save) {
  const adopt = useCallback((fig) => put(fig), [put]);
  const saveFigure = useCallback((fig) => save(fig.id, figureToDoc(fig, memory.stamp(fig.id))), [save, memory]);
  const takenAliases = useCallback(() => [...memory.aliases], [memory]);
  const claimAlias = useCallback((alias) => memory.aliases.add(alias), [memory]);
  return { adopt, saveFigure, takenAliases, claimAlias };
}

/** The two ways figures arrive hidden without a public life first: born hidden, and back from the wings. */
function useArrivals({ put }, memory, writer) {
  /**
   * Born hidden: the ids are hidden BEFORE the roster add, in the same handler,
   * so the push never sees them and the turn tracker's door refuses them. The
   * record is written once the combatant exists (useFollowRosterEdits).
   */
  const reserve = useCallback(
    (ids) => {
      const figs = ids.map((id) => {
        const alias = aliasFor([...memory.aliases]);
        memory.aliases.add(alias);
        return { id, combatant: null, token: null, alias, initiative: null, bornHidden: true };
      });
      put(...figs);
    },
    [put, memory],
  );
  /** Figures back from the wings (a scene cut): hidden at once, before the roster takes them, and stored again. */
  const restore = useCallback(
    (figs) => {
      for (const fig of figs) memory.aliases.add(fig.alias);
      put(...figs);
      for (const fig of figs) writer.queue(fig, memory.stamp(fig.id));
    },
    [put, memory, writer],
  );
  return { reserve, restore };
}

/** The hidden figures' tokens, keyed like the battlefield's, and the verb that moves (or lifts) one. */
function useHiddenTokens(figures, edit) {
  const hiddenTokens = useMemo(() => {
    const placed = [...figures.values()].filter((f) => f.token);
    return placed.length ? Object.fromEntries(placed.map((f) => [f.id, f.token])) : NO_TOKENS;
  }, [figures]);
  const placeToken = useCallback((id, token) => edit(id, { token }), [edit]);
  return { hiddenTokens, placeToken };
}

/**
 * Who is hidden: the set for rendering - keyed on WHO, so an edit to a figure
 * (a drag, a hit) gives back the same set and the shared slice is not rebuilt -
 * and stable readers for verbs that run mid-tick, current the moment a verb
 * commits (the store's ref): the turn tracker's door, a reveal that admits its
 * figure in the tick that ended it, the reveal proposal's alias.
 */
function useHiddenIds({ figures, ref }) {
  const who = [...figures.keys()].join('\u0000');
  const hiddenIds = useMemo(() => (who ? new Set(who.split('\u0000')) : NO_IDS), [who]);
  const isHidden = useCallback((id) => ref.current.has(id), [ref]);
  const aliasOf = useCallback((id) => ref.current.get(id)?.alias ?? null, [ref]);
  return { hiddenIds, isHidden, aliasOf };
}

export default function useHiddenFigures({ sessionId, isGm, roster, setRoster, rosterTabs, mp }) {
  const synced = !!sessionId && !!isGm && !!mp;
  const store = useFigureStore();
  const { figures } = store;
  const memory = useMemory();
  const save = useSave(synced, mp, sessionId, memory);
  const writer = useHiddenWriter(save);
  const edit = useEdit(store, writer, memory);
  const { end, endStored } = useEnd({ store, memory, writer, save });
  useHiddenListener({ synced, mp, sessionId, store, memory, setRoster });
  // The ids seen in the working roster: shared, so a removed figure is ended and never adopted again (see both).
  const seen = useRef(new Set());
  useAdoptIntoRoster(figures, roster, setRoster, seen);
  const ids = useHiddenIds(store);
  const { hiddenPart, ...seam } = useSharedSlice(roster, setRoster, rosterTabs, ids.hiddenIds);
  useFollowRosterEdits(figures, hiddenPart, edit);
  useDropRemoved(roster, figures, end, seen);
  const verbs = { ...useFigureVerbs(store, memory, save), ...useArrivals(store, memory, writer) };
  return { ...seam, ...ids, figures, figuresRef: store.ref, endStored, ...useHiddenTokens(figures, edit), ...verbs };
}
